import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ShieldAlert, Zap, Rocket, TrendingUp, CheckCircle2, Activity, ServerCrash, DollarSign, Calendar } from 'lucide-react';

const formatCurrency = (val, country) => {
  if (!val && val !== 0) return '—';
  
  const mappings = {
    'India': { currency: 'INR', locale: 'en-IN' },
    'Singapore': { currency: 'SGD', locale: 'en-SG' },
    'Australia': { currency: 'AUD', locale: 'en-AU' },
    'New Zealand': { currency: 'NZD', locale: 'en-NZ' },
    'Japan': { currency: 'JPY', locale: 'ja-JP' },
    'South Korea': { currency: 'KRW', locale: 'ko-KR' },
    'Hong Kong': { currency: 'HKD', locale: 'en-HK' },
    'United States': { currency: 'USD', locale: 'en-US' },
    'Canada': { currency: 'CAD', locale: 'en-CA' },
    'United Kingdom': { currency: 'GBP', locale: 'en-GB' },
    'Sweden': { currency: 'SEK', locale: 'sv-SE' },
    'Switzerland': { currency: 'CHF', locale: 'de-CH' },
    'United Arab Emirates': { currency: 'AED', locale: 'en-AE' },
    'Saudi Arabia': { currency: 'SAR', locale: 'en-SA' },
    'South Africa': { currency: 'ZAR', locale: 'en-ZA' }
  };

  const euCountries = [
    'Germany', 'France', 'Netherlands', 'Ireland', 
    'Austria', 'Belgium', 'Cyprus', 'Estonia', 'Finland', 'Greece',
    'Italy', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Portugal',
    'Slovakia', 'Slovenia', 'Spain'
  ];

  let currency = 'USD'; 
  let locale = 'en-US';

  if (mappings[country]) {
    currency = mappings[country].currency;
    locale = mappings[country].locale;
  } else if (euCountries.includes(country)) {
    currency = 'EUR';
    locale = 'en-IE';
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    notation: "compact",
    maximumFractionDigits: 1
  }).format(val);
};

export default function ExecutionRoadmap() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  const [roadmap, setRoadmap] = useState({
    blockers: [],
    quickWins: [],
    strategicBets: [],
    totalSavings: 0,
    readiness: null
  });

  const fetchData = async () => {
    if (!user || !user.org_id) return;

    try {
      setLoading(true);
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const headers = { Authorization: `Bearer ${token}` };

      const [wfRes, progRes, readRes] = await Promise.all([
        axios.get(`${baseURL}/api/sprint/workflow-map/${user.org_id}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${baseURL}/api/progress`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${baseURL}/api/sprint/readiness`, { headers }).catch(() => ({ data: null }))
      ]);

      const workflows = Array.isArray(wfRes.data) ? wfRes.data : [];
      const progressTasks = Array.isArray(progRes.data) ? progRes.data : [];
      const readinessData = readRes.data || null;

      const blockers = progressTasks.filter(p => 
        p.status !== 'Completed' && (p.item_type === 'action_plan' || p.item_type === 'cross_risk')
      );

      const quickWins = workflows.filter(w => w.impact_score >= 5 && w.effort_score < 5).sort((a, b) => b.impact_score - a.impact_score);
      const strategicBets = workflows.filter(w => w.impact_score >= 5 && w.effort_score >= 5).sort((a, b) => b.impact_score - a.impact_score);

      const totalSavings = workflows.filter(w => w.impact_score >= 6).reduce((acc, curr) => acc + ((Number(curr.fte_cost) || 0) * 0.4), 0);

      setRoadmap({ blockers, quickWins, strategicBets, totalSavings, readiness: readinessData });
    } catch (error) {
      console.error("Failed to load roadmap data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.org_id, token]);

  const score = roadmap.readiness?.readiness_score || 0;
  const isHighRisk = score > 0 && score < 50;

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] w-full">
      <motion.div 
        animate={{ scale: [1, 1.1, 1], rotate: [0, 90, 180, 270, 360] }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
        className="w-16 h-16 bg-gradient-to-tr from-emerald-600 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/30 mb-6"
      >
        <Calendar className="w-8 h-8 text-white" />
      </motion.div>
      <h3 className="text-xl font-bold text-slate-800 tracking-tight">Generating Trinity Roadmap...</h3>
      <p className="text-slate-500 text-sm mt-2">Aligning Architecture, Risk, and Value metrics.</p>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6 pb-24 overflow-hidden">
      
      {/* HEADER SECTION - Structure protected, contents animated */}
      <div className="flex flex-col md:flex-row justify-between md:items-end mb-10 border-b border-slate-200 pb-6 gap-4">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">6-Month Execution Roadmap</h1>
          <p className="text-slate-500 mt-2 font-medium">Your prioritized deployment timeline, driven by your architecture and risk posture.</p>
        </motion.div>
        
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="flex gap-4 md:gap-6 text-right">
          {roadmap.readiness && (
            <motion.div whileHover={{ scale: 1.02 }} className="bg-slate-50 p-4 rounded-2xl border border-slate-200 shadow-sm transition-all cursor-default">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center justify-end gap-1"><Activity className="w-3 h-3"/> Readiness</p>
              <h2 className={`text-2xl font-black tracking-tight ${score >= 80 ? 'text-emerald-600' : score >= 50 ? 'text-amber-500' : 'text-red-600'}`}>
                {score} <span className="text-sm font-bold text-slate-400">/ 100</span>
              </h2>
            </motion.div>
          )}
          <motion.div whileHover={{ scale: 1.02 }} className="bg-gradient-to-br from-emerald-50 to-teal-50 p-4 rounded-2xl border border-emerald-100 shadow-sm transition-all cursor-default">
            <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1 flex items-center justify-end gap-1"><DollarSign className="w-3 h-3"/> Target ROI</p>
            <h2 className="text-2xl font-black text-emerald-700 tracking-tight">{formatCurrency(roadmap.totalSavings, user?.country)}</h2>
          </motion.div>
        </motion.div>
      </div>

      {/* THE TIMELINE STRUCTURE - Standard Tailwind, No Framer Wrappers to protect layout */}
      <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent">

        {/* ── PHASE 0: FOUNDATION & GOVERNANCE ── */}
        <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full border-4 border-white shadow-md shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10 transition-transform duration-300 group-hover:scale-110 ${isHighRisk ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-600'}`}>
            <ShieldAlert className="w-5 h-5" />
          </div>
          
          <motion.div 
            initial={{ opacity: 0, x: 20 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            viewport={{ once: true }}
            className={`w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-6 rounded-3xl border bg-white shadow-sm hover:shadow-md transition-shadow duration-300 ${isHighRisk ? 'border-red-300' : 'border-slate-200'}`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className={`text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-md ${isHighRisk ? 'text-red-700 bg-red-50' : 'text-slate-600 bg-slate-100'}`}>Phase 0 • Immediate</span>
            </div>
            <h3 className="font-bold text-xl text-slate-900 mb-2">Foundation & Governance</h3>
            <p className="text-sm text-slate-600 mb-5 font-medium leading-relaxed">Mandatory compliance and architectural actions required before safe deployment.</p>
            
            <ul className="space-y-3">
              {isHighRisk && (
                <li className="text-sm font-medium text-red-800 flex items-start gap-3 bg-red-50/50 p-4 rounded-xl border border-red-100 shadow-sm">
                  <ServerCrash className="w-5 h-5 shrink-0 text-red-500 mt-0.5" />
                  <div>
                    <strong className="block mb-1">Architecture Critical:</strong> Your Readiness score ({score}/100) indicates structural gaps. You must resolve infrastructure and data fragmentation before attempting AI pilots.
                  </div>
                </li>
              )}

              {roadmap.blockers.length === 0 ? (
                <li className="flex items-center gap-2 text-sm font-bold text-emerald-700 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100 shadow-sm">
                  <CheckCircle2 className="w-5 h-5" /> All critical compliance gaps resolved.
                </li>
              ) : (
                roadmap.blockers.slice(0, 3).map((blocker, idx) => (
                  <li key={idx} className="text-sm font-medium text-slate-700 flex items-start gap-3 bg-slate-50 p-3.5 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors">
                    <span className="text-amber-500 mt-0.5">⚠️</span> {blocker.item_title}
                  </li>
                ))
              )}
            </ul>
          </motion.div>
        </div>

        {/* ── PHASE 1: QUICK WINS ── */}
        <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
          <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-emerald-100 text-emerald-600 shadow-md shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10 transition-transform duration-300 group-hover:scale-110">
            <Zap className="w-5 h-5" />
          </div>

          <motion.div 
            initial={{ opacity: 0, x: -20 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            viewport={{ once: true }}
            className={`w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-6 rounded-3xl border shadow-sm hover:shadow-md transition-shadow duration-300 ${isHighRisk ? 'bg-slate-50 border-slate-200 opacity-75' : 'bg-white border-emerald-200'}`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-emerald-700 uppercase tracking-widest bg-emerald-50 px-3 py-1 rounded-md border border-emerald-100">Phase 1 • Months 1-2</span>
            </div>
            <h3 className="font-bold text-xl text-slate-900 mb-2">Execute "Quick Wins"</h3>
            
            {isHighRisk && (
              <div className="text-xs font-bold text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-200 mb-4 flex items-center gap-2 shadow-sm">
                 <Activity className="w-4 h-4 shrink-0" /> Recommended to delay Phase 1 until Phase 0 Foundation is stabilized.
              </div>
            )}

            <p className="text-sm text-slate-600 mb-5 font-medium leading-relaxed">High-impact, low-effort workflows ready for immediate AI augmentation.</p>
            
            {roadmap.quickWins.length === 0 ? (
              <p className="text-sm text-slate-500 italic bg-slate-50 p-4 rounded-xl border border-slate-100">No low-effort Quick Wins identified in current inventory.</p>
            ) : (
              <div className="space-y-3">
                {roadmap.quickWins.map((wf, idx) => {
                  const estSavings = wf.impact_score >= 6 ? (Number(wf.fte_cost) * 0.4) : 0;
                  return (
                    <motion.div whileHover={{ scale: 1.02 }} key={idx} className="group/item bg-white border border-slate-100 p-4 rounded-2xl flex justify-between items-center shadow-sm hover:shadow hover:border-emerald-200 transition-all duration-200 cursor-default">
                      <div>
                        <p className="text-sm font-bold text-slate-800 leading-tight">{wf.process_name}</p>
                        <p className="text-xs text-indigo-600 font-bold mt-1.5">{wf.ai_suitability}</p>
                      </div>
                      {estSavings > 0 && (
                        <div className="text-right pl-3">
                          <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Est. Value</span>
                          <span className="text-sm font-black text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md">{formatCurrency(estSavings, user?.country)}</span>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </div>

        {/* ── PHASE 2: STRATEGIC BETS ── */}
        <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
          <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-blue-100 text-blue-600 shadow-md shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10 transition-transform duration-300 group-hover:scale-110">
            <Rocket className="w-5 h-5" />
          </div>

          <motion.div 
            initial={{ opacity: 0, x: 20 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            viewport={{ once: true }}
            className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-6 rounded-3xl border border-blue-200 bg-white shadow-sm hover:shadow-md transition-shadow duration-300"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-blue-700 uppercase tracking-widest bg-blue-50 px-3 py-1 rounded-md border border-blue-100">Phase 2 • Months 3-6</span>
            </div>
            <h3 className="font-bold text-xl text-slate-900 mb-2">Scaled Deployment</h3>
            <p className="text-sm text-slate-600 mb-5 font-medium leading-relaxed">High-impact, higher-effort strategic bets requiring deep integration.</p>
            
            {roadmap.strategicBets.length === 0 ? (
              <p className="text-sm text-slate-500 italic bg-slate-50 p-4 rounded-xl border border-slate-100">No complex Strategic Bets identified in current inventory.</p>
            ) : (
              <div className="space-y-3">
                {roadmap.strategicBets.map((wf, idx) => {
                  const estSavings = wf.impact_score >= 6 ? (Number(wf.fte_cost) * 0.4) : 0;
                  return (
                    <motion.div whileHover={{ scale: 1.02 }} key={idx} className="group/item bg-slate-50/50 border border-slate-100 p-4 rounded-2xl flex justify-between items-center shadow-sm hover:shadow hover:bg-white hover:border-blue-200 transition-all duration-200 cursor-default">
                      <div>
                        <p className="text-sm font-bold text-slate-800 leading-tight">{wf.process_name}</p>
                        <p className="text-xs text-indigo-600 font-bold mt-1.5">{wf.ai_suitability}</p>
                      </div>
                      {estSavings > 0 && (
                        <div className="text-right pl-3">
                          <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Est. Value</span>
                          <span className="text-sm font-black text-blue-600 bg-blue-50 px-2 py-1 rounded-md">{formatCurrency(estSavings, user?.country)}</span>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </div>

        {/* ── PHASE 3: ONGOING ── */}
        <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group">
          <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-slate-100 text-slate-400 shadow-md shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10 transition-transform duration-300 group-hover:scale-110">
            <TrendingUp className="w-5 h-5" />
          </div>

          <motion.div 
            initial={{ opacity: 0, x: -20 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            viewport={{ once: true }}
            className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-6 rounded-3xl border border-slate-200 bg-slate-50 shadow-sm opacity-90 hover:opacity-100 transition-opacity duration-300"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-widest bg-slate-200 px-3 py-1 rounded-md">Phase 3 • Month 6+</span>
            </div>
            <h3 className="font-bold text-xl text-slate-700 mb-2">Continuous Optimization</h3>
            <p className="text-sm text-slate-500 font-medium leading-relaxed">Monitor model drift, audit Shadow AI, and retrain fine-tuned deployments using the FCAIO Zero-Trust Gateway.</p>
          </motion.div>
        </div>

      </div>
    </div>
  );
}